import React, { useState, useRef, useEffect } from "react";
import SignatureCanvas from "react-signature-canvas";
import CustomButton from "./CustomButton";
import { SOURCE_OF_WEALTH_OPTIONS } from "../utils/sourceOfWealth";
import { createCtsToday, signCts } from "../utils/apiClient";

/**
 * Dialog to create today's CTS (Customer Transaction Sheet)
 * Mirrors Flutter cts_creation_dialog.dart logic exactly
 * @author MFG Invest Team
 */
export default function CtsCreationDialog({
  open,
  onClose,
  onSuccess,
  initialSourceOfFunds = null,
}) {
  const [selectedSourceOfFunds, setSelectedSourceOfFunds] = useState(initialSourceOfFunds);
  const [initialSourceOfFundsState, setInitialSourceOfFundsState] = useState(initialSourceOfFunds);
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [attachmentName, setAttachmentName] = useState("");
  const [showAttachmentSection, setShowAttachmentSection] = useState(initialSourceOfFunds == null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const sigRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    setInitialSourceOfFundsState(initialSourceOfFunds);
    setSelectedSourceOfFunds(initialSourceOfFunds);
    setShowAttachmentSection(initialSourceOfFunds == null);
    setAttachmentFile(null);
    setAttachmentName("");
    setError(null);
    if (sigRef.current) sigRef.current.clear();
  }, [open, initialSourceOfFunds]);

  const handleSourceChange = (e) => {
    const value = e.target.value === "" ? null : Number(e.target.value);
    setSelectedSourceOfFunds(value);
    
    if (initialSourceOfFundsState == null) {
      setShowAttachmentSection(true);
    } else {
      const requiresAttachment = value != null && value !== initialSourceOfFundsState;
      setShowAttachmentSection(requiresAttachment);
      if (!requiresAttachment) {
        setAttachmentFile(null);
        setAttachmentName("");
      }
    }
  };

  const handleAttachmentChange = (e) => {
    const f = e.target.files?.[0];
    if (f) {
      setAttachmentFile(f);
      setAttachmentName(f.name);
    }
  };

  const handleRemoveAttachment = () => {
    setAttachmentFile(null);
    setAttachmentName("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleClearSignature = () => {
    if (sigRef.current) sigRef.current.clear();
  };

  const handleUndoSignature = () => {
    if (sigRef.current) {
      // SignatureCanvas doesn't have undo, so we use clear
      sigRef.current.clear();
    }
  };

  const dataUrlToFile = (dataUrl, name = "signature.png") => {
    // Decode base64 directly (matches Flutter's approach)
    const arr = dataUrl.split(',');
    const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/png';
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], name, { type: mime });
  };

  const handleSubmit = async () => {
    setError(null);

    // Validate form
    if (selectedSourceOfFunds == null) {
      setError("Please select source of funds.");
      return;
    }

    // Attachment is mandatory when creating CTS in the following cases:
    // 1) There is NO initial source of funds (user must always attach)
    // 2) User changed the source of funds from the initial value
    const requiresAttachment = initialSourceOfFundsState == null ||
        selectedSourceOfFunds !== initialSourceOfFundsState;

    if (requiresAttachment && attachmentFile == null) {
      setError("Please attach the required document.");
      return;
    }

    if (!sigRef.current || sigRef.current.isEmpty()) {
      setError("Please provide your signature.");
      return;
    }

    setSubmitting(true);

    try {
      // Convert signature to image (matches Flutter's toPngBytes)
      const dataUrl = sigRef.current.toDataURL("image/png");
      let signatureFile;
      try {
        signatureFile = dataUrlToFile(dataUrl, `signature_${Date.now()}.png`);
        
        // Validate signature file was created correctly
        if (!signatureFile || !(signatureFile instanceof File)) {
          setError("Failed to generate signature file.");
          setSubmitting(false);
          return;
        }
        
        // Verify file has content (size > 0)
        if (signatureFile.size === 0) {
          setError("Signature file is empty. Please draw your signature again.");
          setSubmitting(false);
          return;
        }
      } catch (e) {
        setError("Failed to generate signature.");
        setSubmitting(false);
        return;
      }

      // Prepare data (matches Flutter's data structure)
      const data = {
        SOURCE_OF_FUNDS: selectedSourceOfFunds,
        TOTAL_AMOUNT: 0.0,
      };

      // Step 1: Create CTS (without signature first)
      // Matches Flutter lines 380-390
      const createFileData = {};
      if (attachmentFile != null) {
        createFileData.ATTACHMENT = attachmentFile;
      }

      const createRes = await createCtsToday(
        { sourceOfFunds: selectedSourceOfFunds, totalAmount: 0 },
        createFileData
      );
      
      // Matches Flutter lines 392-400: Check if response is null
      if (!createRes) {
        setError("Error creating CTS. No response from server.");
        setSubmitting(false);
        return;
      }

      const d = createRes.decryptedData || createRes.data || {};
      
      // Matches Flutter lines 402-420
      // Check CODE field (not HTTP status code) - server can return 200 HTTP with error CODE
      const code = Number(d.CODE);
      if (code === 200 || code === 205) {
        // CTS created or already exists, now sign it
        // Matches Flutter line 405
        await handleSignCts(signatureFile);
      } else if (code === 206) {
        setError("CTS not found. Please try again.");
        setSubmitting(false);
        return;
      } else {
        // Show the actual error message from server if available
        const errorMsg = d.MESSAGE || `Error creating CTS (Code: ${code || 'unknown'}). Please try again.`;
        setError(errorMsg);
        setSubmitting(false);
        return;
      }
    } catch (e) {
      setError(e.message || "Error creating CTS. Please try again.");
      setSubmitting(false);
    }
  };

  const handleSignCts = async (signatureFile) => {
    // Matches Flutter's _signCts method (lines 431-465)
    // Note: Since we're using CREATE_CTS_TODAY instead of SIGN_CTS,
    // we may need to send source of funds when signing
    try {
      const signRes = await signCts(signatureFile, selectedSourceOfFunds);
      
      // Matches Flutter lines 449-464
      if (!signRes) {
        setError("Error signing CTS. No response from server.");
        setSubmitting(false);
        return;
      }

      const sd = signRes.decryptedData || signRes.data || {};
      const code = Number(sd.CODE);
      
      // Matches Flutter lines 449-451
      if (code === 200 || code === 205) {
        // Success
        if (onSuccess) onSuccess();
        if (onClose) onClose();
      } else {
        // Show the actual error message from server if available
        const errorMsg = sd.MESSAGE || `Error signing CTS (Code: ${code || 'unknown'}). Please try again.`;
        setError(errorMsg);
        setSubmitting(false);
      }
    } catch (e) {
      setError(e.message || "Error signing CTS. Please try again.");
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="cts-dialog-overlay">
      <div className="cts-dialog">
        <div className="cts-dialog-header">
          <h2 className="cts-dialog-title">Create CTS</h2>
          <button type="button" className="cts-dialog-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        <div className="cts-dialog-body">
          <div className="cts-form-group">
            <label className="cts-label">Source of funds</label>
            <select
              className="cts-select"
              value={selectedSourceOfFunds ?? ""}
              onChange={handleSourceChange}
            >
              <option value="">Select source of funds</option>
              {SOURCE_OF_WEALTH_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          {showAttachmentSection && (
            <div className="cts-form-group">
              <label className="cts-label">Attachment</label>
              {!attachmentFile ? (
                <div className="cts-attach-row">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleAttachmentChange}
                    className="cts-file-input"
                  />
                  <button type="button" className="cts-btn-attach" onClick={() => fileInputRef.current?.click()}>
                    Select file
                  </button>
                </div>
              ) : (
                <div className="cts-attach-selected">
                  <span className="cts-attach-name">{attachmentName}</span>
                  <button type="button" className="cts-btn-remove" onClick={handleRemoveAttachment}>
                    Remove
                  </button>
                </div>
              )}
            </div>
          )}

          <div className="cts-form-group">
            <label className="cts-label">Signature</label>
            <div className="cts-signature-wrap">
              <SignatureCanvas
                ref={sigRef}
                canvasProps={{ className: "cts-signature-canvas", width: 500, height: 200 }}
                backgroundColor="#ffffff"
                penColor="#141414"
              />
              <div className="cts-signature-actions">
                <button type="button" className="cts-btn-undo" onClick={handleUndoSignature}>
                  Undo
                </button>
                <button type="button" className="cts-btn-clear" onClick={handleClearSignature}>
                  Clear
                </button>
              </div>
            </div>
          </div>

          {error && <div className="cts-error">{error}</div>}
        </div>
        <div className="cts-dialog-footer">
          <button type="button" className="cts-btn-cancel" onClick={onClose}>
            Cancel
          </button>
          <CustomButton variant="primary" className="cts-btn-submit" onClick={handleSubmit} disabled={submitting}>
            {submitting ? "Please wait…" : "Submit CTS"}
          </CustomButton>
        </div>
      </div>
    </div>
  );
}

